import { getColorFromId } from "@nova/util-strings"
import { Marker } from "react-map-gl"
import { memo, useEffect, useMemo, useState } from "react"
import { Cursor, LiveChatPayload, zIndex } from "@nova/constants"
import { useAppSelector } from "integrations/redux/hooks"
import { selectViewers, selectWatching } from "features/pubsub/slice"
import { createPortal } from "react-dom"
import { socket } from "integrations/redux/store"
import { MapboxCursor } from "./MapboxCursor"

const colorByUserId: Record<string, string> = {}

const zIndexByHoveredId: Record<string, number> = {
    "side-panel-pubsub": zIndex.SidePanel + 20000,
    "inspector-panel-pubsub": zIndex.InspectorPanel + 10000,
}

const FADE_OUT_DELAY = 5000

const MapboxCursors = memo(() => {
    const watching = useAppSelector(selectWatching())
    const viewers = useAppSelector(selectViewers())
    const viewerSocketIds = useMemo(() => new Set(viewers.map((viewer) => viewer.id)), [viewers])
    const [cursorData, setCursorData] = useState<Cursor[]>([])
    const [liveChatData, setLiveChatData] = useState<LiveChatPayload[]>([])

    const liveChatDataByUserId = useMemo(
        () =>
            liveChatData.reduce(
                (acc, cur) => {
                    acc[cur.userId] = cur
                    return acc
                },
                {} as Record<string, LiveChatPayload>
            ),
        [liveChatData]
    )

    // Subscribe to socket events.
    useEffect(() => {
        socket?.on("updatedMouse", (data: Cursor) => {
            if (data.userId) colorByUserId[data.userId] ??= getColorFromId(data.userId)

            setCursorData((prevCursorData) => {
                const index = prevCursorData.findIndex((user) => user.id === data.id)
                if (index !== -1) {
                    // Replace the existing item
                    return prevCursorData.map((item) => (item.id === data.id ? data : item))
                } else {
                    // Append new item
                    return [...prevCursorData, data]
                }
            })
        })

        socket?.on("updatedLiveChat", (data: LiveChatPayload) => {
            setLiveChatData((prevLiveChatData) => {
                const index = prevLiveChatData.findIndex((user) => user.id === data.id)
                if (index !== -1) {
                    // Replace the existing item
                    return prevLiveChatData.map((item) => (item.id === data.id ? data : item))
                } else {
                    // Append new item
                    return [...prevLiveChatData, data]
                }
            })
        })

        return () => {
            socket?.off("updatedMouse")
            socket?.off("updatedLiveChat")
        }
    }, [])

    return (
        <>
            {/* Other Users' Cursors */}
            {cursorData.map((cursor) => {
                const cursorZIndex =
                    (cursor.mouse.id && zIndexByHoveredId[cursor.mouse.id]) || zIndex.PubsubCursor

                const liveChatData = liveChatDataByUserId[cursor.userId]
                const isWithin5Seconds =
                    liveChatData && Date.now() - liveChatData.updatedAt >= FADE_OUT_DELAY

                return viewerSocketIds.has(cursor.id) ? (
                    // Cursor on top of HTML elements.
                    cursor.mouse.id ? (
                        watching?.id === cursor.id && document.getElementById(cursor.mouse.id) ? (
                            createPortal(
                                <div
                                    style={{
                                        position: "absolute",
                                        left: cursor.mouse.coordinates[0],
                                        top: cursor.mouse.coordinates[1],
                                    }}
                                >
                                    <MapboxCursor
                                        color={colorByUserId[cursor.userId]}
                                        name={cursor.name}
                                        zIndex={cursorZIndex}
                                        liveChat={isWithin5Seconds ? liveChatData : undefined}
                                    />
                                </div>,
                                document.getElementById(cursor.mouse.id) as HTMLElement
                            )
                        ) : null
                    ) : (
                        // Cursor on map.
                        <Marker
                            key={cursor.id}
                            longitude={cursor.mouse.coordinates[0]}
                            latitude={cursor.mouse.coordinates[1]}
                            draggable={false}
                            className="pointer-events-none"
                            style={{
                                position: "absolute",
                                zIndex: cursorZIndex,
                            }}
                            anchor="top-left"
                        >
                            <MapboxCursor
                                color={colorByUserId[cursor.userId]}
                                name={cursor.name}
                                zIndex={cursorZIndex}
                                liveChat={liveChatDataByUserId[cursor.userId]}
                            />
                        </Marker>
                    )
                ) : null
            })}
        </>
    )
})

MapboxCursors.displayName = "MapboxCursors"
export { MapboxCursors }
